import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './VerifyEmailPage.css';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setVerificationStatus('error');
        setMessage('Invalid verification link. Please request a new verification email.');
        return;
      }

      try {
        const response = await authApi.verifyEmail(token);
        
        // Update the user context with the verified user data
        updateUser(response.data.user);
        
        setVerificationStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
        
        // Redirect to home page after 3 seconds
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
        
      } catch (error) {
        setVerificationStatus('error');
        setMessage(
          error.response?.data?.error || 
          'Verification failed. The link may have expired or is invalid. Please request a new verification email.'
        );
      }
    };

    verifyEmail();
  }, [searchParams, navigate, updateUser]);

  const handleResendVerification = async () => {
    setVerificationStatus('verifying');
    setMessage('Sending new verification email...');
    
    try {
      // We need to get the user's email from the context or redirect to login
      navigate('/auth?message=Please sign in to resend verification email');
    } catch {
      setVerificationStatus('error');
      setMessage('Failed to resend verification email. Please try again.');
    }
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleGoToAuth = () => {
    navigate('/auth', { replace: true });
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-container">
        <div className="verify-email-card">
          <div className="verify-email-header">
            <h1>Verify your email</h1>
          </div>

          <div className="verify-email-content">
            {verificationStatus === 'verifying' && (
              <div className="verifying-state">
                <LoadingSpinner size="large" />
                <h2>Verifying your email...</h2>
                <p>Please wait while we verify your email address.</p>
              </div>
            )}

            {verificationStatus === 'success' && (
              <div className="success-state">
                <div className="success-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <h2>Email verified</h2>
                <p>{message}</p>
                <p className="redirect-message">
                  You will be redirected to the home page in a few seconds...
                </p>
                <button onClick={handleGoHome} className="primary-button">
                  Go to home
                </button>
              </div>
            )}

            {verificationStatus === 'error' && (
              <div className="error-state">
                <div className="error-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <h2>Verification failed</h2>
                <p>{message}</p>
                <div className="error-actions">
                  <button onClick={handleGoToAuth} className="primary-button">
                    Go to sign in
                  </button>
                  <button onClick={handleResendVerification} className="secondary-button">
                    Request a new email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
